import React from "react";
import {useAppDispatch, useAppSelector} from "../../../../app/store";
import {QuestionSelectOption} from "../../../../types/admin";
import {v4 as uuidv4} from "uuid";
import {ADMIN_LANG, AVAILABLE_LANGUAGES} from "../../../../settings";
import {changeQuestionModal} from "../../../../app/slices/modals/questionEditModal";
import {PlusCircle, Trash} from "react-bootstrap-icons";
import {Col, Form, Row} from "react-bootstrap";

type Props = {
    language: string;
    is_draft: boolean;
};

const QuestionEditSelect = ({language, is_draft}: Props) => {
    const {select_options, multiple, shuffle} = useAppSelector((state) => state.questionEditModal);
    const dispatch = useAppDispatch();

    const handleAddOption = () => {
        if (!is_draft) return; // Prevent adding options if not in draft mode
        const newOption: QuestionSelectOption = {
            option_id: uuidv4(),
            key: uuidv4(),
            points: 0,
            languages: AVAILABLE_LANGUAGES.map((lang) => ({
                language_code: lang,
                option: '',
            })),
        };
        dispatch(changeQuestionModal({select_options: [...select_options, newOption]}));
    };

    const handleRemoveOption = (id: string) => {
        if (!is_draft) return; // Prevent removing options if not in draft mode
        dispatch(changeQuestionModal({select_options: select_options.filter((option) => option.option_id !== id)}));
    };

    const handleOptionTitleChange = (id: string, lang: string, title: string) => {
        const updatedOptions = select_options.map((option) => {
            if (option.option_id === id) {
                return {
                    ...option,
                    languages: option.languages.map((languageOption) =>
                        languageOption.language_code === lang
                            ? {...languageOption, option: title}
                            : languageOption
                    ),
                };
            }
            return option;
        });
        dispatch(changeQuestionModal({select_options: updatedOptions}));
    };

    const handleOptionPointsChange = (id: string, points: number) => {
        const updatedOptions = select_options.map((option) =>
            option.option_id === id ? {...option, points} : option
        );
        dispatch(changeQuestionModal({select_options: updatedOptions}));
    };

    const handleMultipleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(changeQuestionModal({multiple: e.target.checked}));
    };

    const handleShuffleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(changeQuestionModal({shuffle: e.target.checked}));
    }

    return (
        <Col xs={12}>

            <Form.Group>
                <Form.Switch
                    className='form-label'
                    id="multiple"
                    checked={multiple}
                    onChange={handleMultipleChange}
                    disabled={!is_draft}
                    label={ADMIN_LANG.multiple}
                />
            </Form.Group>
            <Form.Group>
                <Form.Switch
                    className='form-label'
                    id="shuffle"
                    checked={shuffle}
                    onChange={handleShuffleChange}
                    disabled={!is_draft}
                    label={ADMIN_LANG.shuffle_options}
                />
            </Form.Group>
            <h4 className="pb-2 pt-3">
                {ADMIN_LANG.select_options}{" "}
                <PlusCircle
                    className={`utility-icon ${is_draft ? "green" : "disabled"}`}
                    onClick={handleAddOption}
                />
            </h4>
            <Row className="gy-3">
                <Col xs={7}>
                    <Form.Label className="mb-0">
                        {`${ADMIN_LANG.title} (${language.toUpperCase()})`}
                    </Form.Label>
                </Col>
                <Col xs={3}>
                    <Form.Label className="mb-0">{ADMIN_LANG.points}</Form.Label>
                </Col>
                {select_options.map((option, index) => {
                    const languageOption = option.languages.find(
                        (lang) => lang.language_code === language
                    );
                    const optionHelpText = option.languages
                        .filter(
                            (lang) =>
                                lang.language_code !== language && lang.option
                        )
                        .map((lang) => (
                            <div key={lang.language_code}>
                                <strong>{lang.language_code.toUpperCase()}: </strong>
                                {lang.option}
                            </div>
                        ));
                    return (
                        <Col xs={12} key={option.key}>
                            <div className="form-group row">
                                <Col xs={7}>
                                    <Form.Group controlId={`option_${index}_${language}`}>
                                        <Form.Control
                                            type="text"
                                            maxLength={255}
                                            value={languageOption?.option || ""}
                                            onChange={(e) =>
                                                handleOptionTitleChange(
                                                    option.option_id!,
                                                    language,
                                                    e.target.value
                                                )
                                            }
                                        />
                                        {optionHelpText.length > 0 &&
                                            <Form.Text className='help-text'>
                                                {optionHelpText}
                                            </Form.Text>
                                        }
                                    </Form.Group>
                                </Col>
                                <Col xs={3}>
                                    <Form.Group controlId={`points_${index}`}>
                                        <Form.Control
                                            type="number"
                                            value={option.points}
                                            onChange={(e) =>
                                                handleOptionPointsChange(
                                                    option.option_id!,
                                                    parseInt(e.target.value)
                                                )
                                            }
                                            disabled={!is_draft}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={2} className="d-flex justify-content-center align-items-center">
                                    <Trash
                                        onClick={() =>
                                            handleRemoveOption(option.option_id!)
                                        }
                                        className={`utility-icon ${
                                            is_draft ? "red" : "disabled"
                                        }`}
                                    />
                                </Col>
                            </div>
                        </Col>
                    );
                })}
            </Row>
        </Col>
    );
};

export default QuestionEditSelect;